import React from 'react';
import { Loader2 } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
  BarChart, Bar,
} from 'recharts';
import { useScreeningAnalytics } from '../../hooks/useScreening';

const COLORS = {
  sanctions: '#ef4444',
  pep: '#f59e0b',
  adverse_media: 'var(--accent-secondary)',
  pending: '#6b7280',
  confirmed_true: '#ef4444',
  confirmed_false: '#22c55e',
};

const PIE_COLORS = ['#6b7280', '#ef4444', '#22c55e', '#f59e0b'];

export default function ScreeningAnalytics({ startDate, endDate, granularity }) {
  const { data, isLoading } = useScreeningAnalytics(startDate, endDate, granularity);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
        <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  const analytics = data || {};
  const resolutionData = Object.entries(analytics.by_resolution || {}).map(([key, value]) => ({
    name: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    value,
  }));
  const sourceData = Object.entries(analytics.by_source || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([key, value]) => ({ name: key, count: value }));

  return (
    <div className="screening-analytics" data-testid="screening-analytics">
      <style>{`
        .screening-analytics {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .sa-kpi-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }

        @media (max-width: 900px) {
          .sa-kpi-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        .sa-kpi-card {
          background: var(--bg-secondary, #111318);
          border: 1px solid var(--border-color, #2a2f3a);
          border-radius: 10px;
          padding: 18px 20px;
        }

        .sa-kpi-label {
          font-size: 12px;
          font-weight: 500;
          color: var(--text-secondary, #8b919e);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 8px;
        }

        .sa-kpi-value {
          font-size: 28px;
          font-weight: 700;
          color: var(--text-primary, #f0f2f5);
          line-height: 1;
        }

        .sa-kpi-sub {
          font-size: 12px;
          color: var(--text-tertiary, #5a5f6b);
          margin-top: 6px;
        }

        .sa-charts-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        @media (max-width: 900px) {
          .sa-charts-grid {
            grid-template-columns: 1fr;
          }
        }

        .sa-chart-card {
          background: var(--bg-secondary, #111318);
          border: 1px solid var(--border-color, #2a2f3a);
          border-radius: 10px;
          padding: 20px;
        }

        .sa-chart-card.full-width {
          grid-column: 1 / -1;
        }

        .sa-chart-title {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          margin: 0 0 16px;
        }

        .sa-entity-table {
          width: 100%;
          border-collapse: collapse;
        }

        .sa-entity-table th {
          font-size: 11px;
          font-weight: 600;
          color: var(--text-secondary, #8b919e);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 8px 12px;
          text-align: left;
          border-bottom: 1px solid var(--border-color, #2a2f3a);
        }

        .sa-entity-table td {
          font-size: 13px;
          color: var(--text-primary, #f0f2f5);
          padding: 10px 12px;
          border-bottom: 1px solid var(--border-subtle, rgba(255,255,255,0.04));
        }

        .sa-entity-table tr:last-child td {
          border-bottom: none;
        }

        .sa-empty {
          text-align: center;
          padding: 40px 20px;
          color: var(--text-secondary, #8b919e);
          font-size: 14px;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* KPI Cards */}
      <div className="sa-kpi-grid">
        <div className="sa-kpi-card" data-testid="kpi-total-checks">
          <div className="sa-kpi-label">Total Checks</div>
          <div className="sa-kpi-value">{(analytics.total_checks ?? 0).toLocaleString()}</div>
        </div>
        <div className="sa-kpi-card" data-testid="kpi-hit-rate">
          <div className="sa-kpi-label">Hit Rate</div>
          <div className="sa-kpi-value">{(analytics.hit_rate ?? 0).toFixed(1)}%</div>
          <div className="sa-kpi-sub">{analytics.total_hits ?? 0} total hits</div>
        </div>
        <div className="sa-kpi-card" data-testid="kpi-fp-rate">
          <div className="sa-kpi-label">False Positive Rate</div>
          <div className="sa-kpi-value">{(analytics.false_positive_rate ?? 0).toFixed(1)}%</div>
        </div>
        <div className="sa-kpi-card" data-testid="kpi-avg-resolution">
          <div className="sa-kpi-label">Avg Resolution Time</div>
          <div className="sa-kpi-value">{(analytics.avg_resolution_time_hours ?? 0).toFixed(1)}h</div>
        </div>
      </div>

      {/* Hits Over Time - Line Chart */}
      <div className="sa-chart-card full-width" data-testid="chart-hits-over-time">
        <h3 className="sa-chart-title">Hits Over Time</h3>
        {(analytics.hits_over_time || []).length > 0 ? (
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={analytics.hits_over_time}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#8b919e' }} />
              <YAxis tick={{ fontSize: 11, fill: '#8b919e' }} />
              <Tooltip
                contentStyle={{ background: '#1a1b23', border: '1px solid #2a2f3a', borderRadius: 8 }}
                labelStyle={{ color: '#f0f2f5' }}
              />
              <Legend />
              <Line type="monotone" dataKey="sanctions" stroke={COLORS.sanctions} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="pep" stroke={COLORS.pep} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="adverse_media" stroke={COLORS.adverse_media} strokeWidth={2} dot={false} name="adverse media" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="sa-empty">No hit data for this period</div>
        )}
      </div>

      <div className="sa-charts-grid">
        {/* Resolution Breakdown - Pie Chart */}
        <div className="sa-chart-card" data-testid="chart-resolution-pie">
          <h3 className="sa-chart-title">Resolution Breakdown</h3>
          {resolutionData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={resolutionData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {resolutionData.map((entry, index) => (
                    <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="sa-empty">No resolution data</div>
          )}
        </div>

        {/* Hits by Source - Bar Chart */}
        <div className="sa-chart-card" data-testid="chart-hits-by-source">
          <h3 className="sa-chart-title">Hits by Source List</h3>
          {sourceData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={sourceData} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#8b919e' }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#8b919e' }} width={80} />
                <Tooltip
                  contentStyle={{ background: '#1a1b23', border: '1px solid #2a2f3a', borderRadius: 8 }}
                />
                <Bar dataKey="count" fill={COLORS.sanctions} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="sa-empty">No source data</div>
          )}
        </div>
      </div>

      {/* Top Matched Entities - Table */}
      <div className="sa-chart-card" data-testid="table-top-entities">
        <h3 className="sa-chart-title">Top Matched Entities</h3>
        {(analytics.top_matched_entities || []).length > 0 ? (
          <table className="sa-entity-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Hits</th>
                <th>Avg Confidence</th>
                <th>Last Seen</th>
              </tr>
            </thead>
            <tbody>
              {analytics.top_matched_entities.map((entity, idx) => (
                <tr key={idx}>
                  <td>{String(entity.name || 'Unknown')}</td>
                  <td>{entity.hit_count ?? 0}</td>
                  <td>{(entity.avg_confidence ?? 0).toFixed(0)}%</td>
                  <td>{entity.last_seen || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="sa-empty">No matched entities yet</div>
        )}
      </div>
    </div>
  );
}
